import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CalendarRange, ChevronDown, PiggyBank, X } from "lucide-react";
import { toast } from "sonner";
import { getMonthlyCategoryTransactions, getMonthlyHistory, getMonthlyReport } from "@/lib/api";
import { formatEUR, formatEUR0 } from "@/lib/format";

const VIOLET = "#A78BFA";
const GREEN = "#00FF87";
const RED = "#FF4D4D";

const savedColor = (v) => (v >= 0 ? GREEN : RED);

const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
};

/* mini grafico a barre del risparmio mese-per-mese */
const SavingsBars = ({ months }) => {
  if (!months || months.length === 0) return null;
  const maxAbs = Math.max(...months.map((m) => Math.abs(m.saved)), 1);
  return (
    <div className="flex items-end justify-between gap-1.5" data-testid="savings-bars" style={{ height: 96 }}>
      {months.map((m) => {
        const h = Math.max((Math.abs(m.saved) / maxAbs) * 78, 3);
        const col = savedColor(m.saved);
        return (
          <div key={m.month} className="flex flex-1 flex-col items-center justify-end gap-1.5" data-testid={`savings-bar-${m.month}`}>
            <span className="mono text-[8px] tracking-tight text-white/40">{formatEUR0(m.saved)}</span>
            <div
              className="w-full rounded-[3px]"
              style={{
                height: h,
                background: m.is_current ? `${col}55` : col,
                border: m.is_current ? `1px dashed ${col}` : "none",
              }}
            />
            <span className="mono text-[9px] uppercase tracking-[0.04em] text-white/45">{m.month_name?.slice(0, 3)}</span>
          </div>
        );
      })}
    </div>
  );
};

export const MonthlyReportSheet = ({ open, onClose, onOpenInvest }) => {
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState(null);
  const [expandedCat, setExpandedCat] = useState(null); // categoria aperta nel drill-down
  const [catTx, setCatTx] = useState({}); // cache: categoria -> {loading, transactions, count, total}

  const load = useCallback(async () => {
    setReport(null);
    setHistory(null);
    setExpandedCat(null);
    setCatTx({});
    try {
      const [r, h] = await Promise.all([getMonthlyReport(), getMonthlyHistory(6)]);
      setReport(r);
      setHistory(h.months || []);
    } catch {
      toast.error("Impossibile caricare il report mensile");
    }
  }, []);

  const toggleCat = async (category) => {
    if (expandedCat === category) {
      setExpandedCat(null);
      return;
    }
    setExpandedCat(category);
    if (!catTx[category] && report?.month) {
      setCatTx((prev) => ({ ...prev, [category]: { loading: true } }));
      try {
        const res = await getMonthlyCategoryTransactions(report.month, category);
        setCatTx((prev) => ({
          ...prev,
          [category]: { loading: false, transactions: res.transactions || [], count: res.count, total: res.total },
        }));
      } catch {
        setCatTx((prev) => ({ ...prev, [category]: { loading: false, transactions: [], count: 0 } }));
        toast.error("Impossibile caricare il dettaglio della categoria");
      }
    }
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      load();
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const plan = report?.plan;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="monthly-report-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 top-12 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <span className="mono flex items-center gap-2 text-sm font-medium tracking-[0.1em]" style={{ color: VIOLET }}>
                <CalendarRange size={15} />
                REPORT MENSILE <span className="text-white/25">//</span> LIQO
              </span>
              <button
                onClick={onClose}
                data-testid="monthly-close-button"
                aria-label="Chiudi"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {report === null ? (
                <div className="space-y-4" data-testid="monthly-loading">
                  <div className="h-32 animate-pulse rounded-[12px] border border-white/[0.06] bg-[#0D0D14]" />
                  <div className="h-28 animate-pulse rounded-[12px] border border-white/[0.06] bg-[#0D0D14]" />
                  <div className="h-32 animate-pulse rounded-[12px] border border-white/[0.06] bg-[#0D0D14]" />
                </div>
              ) : !report.has_data ? (
                <div className="flex flex-col items-center pt-12 text-center" data-testid="monthly-empty">
                  <span className="mono text-2xl" style={{ color: VIOLET }}>Σ</span>
                  <p className="mono mt-4 text-sm font-light text-white/85">Ancora nessun mese completo</p>
                  <p className="mt-2 max-w-[280px] text-[12px] font-light text-white/40">
                    Il resoconto del risparmio appare a inizio mese, una volta chiuso il mese precedente.
                  </p>
                </div>
              ) : (
                <>
                  {/* hero risparmio */}
                  <div
                    className="rounded-[12px] border bg-[#0D0D14] p-4"
                    style={{ borderColor: `${VIOLET}33`, boxShadow: `0 0 0 1px ${VIOLET}14` }}
                  >
                    <p className="caps-tag" style={{ color: VIOLET, opacity: 0.85 }}>
                      Risparmio · {report.label}
                    </p>
                    <p
                      className="mono mt-2 text-[38px] font-light leading-none tracking-[-1.5px] tabular"
                      style={{ color: savedColor(report.saved) }}
                      data-testid="monthly-saved"
                    >
                      {formatEUR0(report.saved)}
                    </p>
                    <div className="mono mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-white/55">
                      <span data-testid="monthly-rate">Tasso di risparmio {report.savings_rate}%</span>
                      {report.delta_saved != null && (
                        <>
                          <span className="text-white/20">·</span>
                          <span style={{ color: savedColor(report.delta_saved) }} data-testid="monthly-delta">
                            {report.delta_saved >= 0 ? "+" : "−"}{formatEUR0(Math.abs(report.delta_saved))} vs {report.prev_label}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-[8px] border border-white/[0.06] bg-[#050508] px-3 py-2.5">
                        <p className="caps-tag text-white/35">Entrate</p>
                        <p className="mono mt-1 text-[14px] font-light tabular" style={{ color: GREEN }}>{formatEUR0(report.income)}</p>
                      </div>
                      <div className="rounded-[8px] border border-white/[0.06] bg-[#050508] px-3 py-2.5">
                        <p className="caps-tag text-white/35">Uscite</p>
                        <p className="mono mt-1 text-[14px] font-light tabular text-[#FF7A9C]">{formatEUR0(report.expenses)}</p>
                      </div>
                    </div>
                  </div>

                  {/* storico mese-per-mese */}
                  {history && history.length > 0 && (
                    <div className="mt-4 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4" data-testid="monthly-history">
                      <p className="caps-tag mb-3 text-white/45">Risparmio · ultimi mesi</p>
                      <SavingsBars months={history} />
                    </div>
                  )}

                  {/* top categorie — toccabili per il drill-down */}
                  {report.top_categories?.length > 0 && (
                    <div className="mt-4 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4" data-testid="monthly-top-categories">
                      <div className="mb-2.5 flex items-baseline justify-between">
                        <p className="caps-tag text-white/45">Dove sono andate le uscite · {report.label}</p>
                        <span className="caps-tag text-white/25" style={{ fontSize: "9px" }}>tocca per dettaglio</span>
                      </div>
                      <div className="space-y-1.5">
                        {report.top_categories.map((c) => {
                          const isOpen = expandedCat === c.category;
                          const detail = catTx[c.category];
                          return (
                            <div key={c.category} className="overflow-hidden rounded-[8px] border border-white/[0.05] bg-[#050508]">
                              <button
                                onClick={() => toggleCat(c.category)}
                                data-testid="monthly-category-row"
                                aria-expanded={isOpen}
                                className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-white/[0.03] active:bg-white/[0.05]"
                              >
                                <span className="flex min-w-0 items-center gap-1.5">
                                  <ChevronDown
                                    size={13}
                                    className={`shrink-0 text-white/30 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                                  />
                                  <span className="truncate text-[12px] font-light text-white/80">{c.category}</span>
                                </span>
                                <span className="mono shrink-0 text-[12px] tabular-nums text-white/55">{formatEUR(c.amount)}</span>
                              </button>
                              {isOpen && (
                                <div className="border-t border-white/[0.05] px-3 py-2" data-testid="monthly-category-detail">
                                  {!detail || detail.loading ? (
                                    <div className="space-y-1.5 py-1">
                                      {[0, 1, 2].map((i) => (
                                        <div key={i} className="h-3 w-full animate-pulse rounded bg-white/[0.05]" />
                                      ))}
                                    </div>
                                  ) : detail.transactions.length === 0 ? (
                                    <p className="py-2 text-center text-[11px] font-light text-white/35">Nessuna transazione</p>
                                  ) : (
                                    <>
                                      {detail.count > detail.transactions.length && (
                                        <p className="caps-tag pb-1.5 text-white/30" style={{ fontSize: "9px" }}>
                                          {detail.transactions.length} di {detail.count} movimenti
                                        </p>
                                      )}
                                      <div className="space-y-1.5">
                                        {detail.transactions.map((t) => (
                                          <div key={t.id} className="flex items-center justify-between gap-2" data-testid="monthly-category-tx">
                                            <span className="flex min-w-0 items-center gap-1.5">
                                              <span className="mono shrink-0 text-[10px] uppercase text-white/30">{fmtDay(t.date)}</span>
                                              <span className="truncate text-[11px] font-light text-white/65">{t.description}</span>
                                            </span>
                                            <span className="mono shrink-0 text-[11px] tabular-nums text-white/60">{formatEUR(t.amount)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* piano d'investimento */}
                  {plan && plan.eligible ? (
                    <div
                      className="mt-4 rounded-[12px] border bg-[#0D0D14] p-4"
                      style={{ borderColor: `${GREEN}2E`, boxShadow: `0 0 0 1px ${GREEN}12` }}
                      data-testid="monthly-invest-plan"
                    >
                      <p className="caps-tag flex items-center gap-1.5" style={{ color: GREEN, opacity: 0.9 }}>
                        <PiggyBank size={13} /> Piano d'investimento proposto
                      </p>
                      <p className="mt-2 text-[13px] font-light leading-[1.55] text-white/70">
                        Potresti destinare il risparmio di {report.month_name} ({formatEUR0(plan.amount)}) secondo il profilo{" "}
                        <span className="text-white/90">{plan.risk_label}</span>:
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="rounded-[8px] border border-white/[0.06] bg-[#050508] px-3 py-2.5">
                          <p className="caps-tag text-white/35">Azionario {plan.azionario_pct}%</p>
                          <p className="mono mt-1 text-[14px] font-light tabular text-white/90">{formatEUR0(plan.azionario_eur)}</p>
                        </div>
                        <div className="rounded-[8px] border border-white/[0.06] bg-[#050508] px-3 py-2.5">
                          <p className="caps-tag text-white/35">Obbligaz. {plan.obbligazionario_pct}%</p>
                          <p className="mono mt-1 text-[14px] font-light tabular text-white/90">{formatEUR0(plan.obbligazionario_eur)}</p>
                        </div>
                      </div>
                      {plan.pac_suggested > 0 && (
                        <p className="mono mt-2.5 text-[11px] text-white/45">
                          PAC suggerito {plan.risk_label}: {formatEUR0(plan.pac_suggested)}/mese
                        </p>
                      )}
                      <button
                        onClick={() => {
                          onClose();
                          onOpenInvest?.();
                        }}
                        data-testid="monthly-open-invest"
                        className="mono mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-[8px] border text-[11px] font-medium uppercase tracking-[0.16em] active:scale-[0.98]"
                        style={{ borderColor: `${GREEN}55`, color: GREEN, boxShadow: `0 0 20px ${GREEN}14` }}
                      >
                        Apri investimenti <ArrowRight size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4 text-[12px] font-light leading-[1.55] text-white/50" data-testid="monthly-no-plan">
                      Questo mese non è emerso un risparmio sufficiente da destinare a un piano d'investimento. Tieni d'occhio il ritmo di spesa per liberare margine.
                    </div>
                  )}

                  <p className="mono mt-4 px-1 text-center text-[10px] leading-4 tracking-[0.04em] text-white/25" data-testid="monthly-disclaimer">
                    {report.disclaimer}
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
